import {
  AdminSpinner,
  ComparedPeriodType,
  Div,
  DragScrollX,
  FinancesComparedOverviewEntity,
  FinancesOverviewEntity,
  formatAmount,
  GroupByType,
  lightTheme,
  NumberType,
  renderDateLabelWithToday,
  ReusableChart,
} from '@vgrubs/components';
import { useState } from 'react';
import {
  buildSingleMetricCompareTooltip,
  createField,
  CurrencyYAxisTick,
  CustomXAxisTick,
} from './chartHelpers';
import { GrossCard } from './GrossCard';
import { OperatingExpensesCard } from './OperatingExpensesCard';
import { ProfitAndLossChart } from './ProfitAndLossChart';
import { SaasDetailsModal } from './SaasDetails/SaasDetailsModal';
import { ServiceFeeDetailsModal } from './ServiceFeeDetails/ServiceFeeDetailsModal';
import { SeeDeliveryLossesButton } from './SeeDeliveryLossesButton';
import { SeeLossOrdersButton } from './SeeLossOrdersButton';
import { TotalIncomeCard } from './TotalIncomeCard';

/**
 * Top-level row of Finances charts on the admin dashboard.
 *
 * The API exposes two shapes:
 *   - `/v1/finances`         → `FinancesOverviewEntity` (single period)
 *   - `/v1/finances/compare` → `FinancesComparedOverviewEntity` (per-metric
 *     `{ total, totalToCompare, series:[{key,value,valueToCompare}] }`).
 *
 * We render the same set of charts from whichever shape arrived. Money fields
 * are in cents and converted to dollars at the boundary before being fed to
 * the chart components. The services pie has no comparison counterpart on
 * the backend, so it's hidden in comparison mode.
 */
export const FinancesCharts = ({
  data,
  comparedData,
  loading,
  groupByType = GroupByType.DAY,
  periodTypeComparedTo,
  from,
  to,
  isAdmin = true,
  onDataChange,
}: {
  data?: FinancesOverviewEntity | null;
  comparedData?: FinancesComparedOverviewEntity | null;
  loading?: boolean;
  groupByType?: GroupByType;
  periodTypeComparedTo?: ComparedPeriodType;
  /** Active date range — forwarded to the loss-orders modal. */
  from: string;
  to: string;
  /**
   * Full chart row is admin-only. Support sees just the two loss charts
   * (VR Order Loss, Delivery Loss).
   */
  isAdmin?: boolean;
  /** Refetch the overview — called after a custom-expense create/delete. */
  onDataChange: () => void;
}) => {
  const isComparing =
    periodTypeComparedTo === ComparedPeriodType.LAST_MONTH ||
    periodTypeComparedTo === ComparedPeriodType.LAST_YEAR ||
    periodTypeComparedTo === ComparedPeriodType.CUSTOM;

  // ── Single-period series (cents / counts) ────────────────────────────────
  // Money stays in cents end-to-end; formatAmount / the Y-axis tick divide by
  // 100 when rendering. The chart's recharts internals don't care about units.
  // Income is clamped at 0 — the API can return small negative values from
  // refunds/adjustments, which would render as a "green dip" below the
  // baseline and confuse the headline.
  const clampPositive = (v: number) => Math.max(0, v);
  const grossSeries = (data?.series.gross || []).map((d) => ({
    key: d.key,
    gross: clampPositive(d.value),
  }));
  const incomeSeries = (data?.series.totalIncome || []).map((d) => ({
    key: d.key,
    totalIncome: clampPositive(d.value),
  }));
  const expensesSeries = (data?.series.expenses || []).map((d) => ({
    key: d.key,
    expenses: d.value,
  }));
  const lossSeries = (data?.series.totalLoss || []).map((d) => ({
    key: d.key,
    totalLoss: d.value,
  }));
  const deliveryLossSeries = (data?.series.deliveryLoss || []).map((d) => ({
    key: d.key,
    deliveryLoss: d.value,
  }));

  // ── Comparison series (current vs past, aligned by index) ────────────────
  const grossSeriesCompared = (comparedData?.gross.series || []).map((p) => ({
    key: p.key,
    gross: clampPositive(p.value),
    grossToCompare: clampPositive(p.valueToCompare),
  }));
  const incomeSeriesCompared = (comparedData?.totalIncome.series || []).map(
    (p) => ({
      key: p.key,
      totalIncome: clampPositive(p.value),
      totalIncomeToCompare: clampPositive(p.valueToCompare),
    })
  );
  const expensesSeriesCompared = (comparedData?.expenses.series || []).map(
    (p) => ({
      key: p.key,
      expenses: p.value,
      expensesToCompare: p.valueToCompare,
    })
  );
  const lossSeriesCompared = (comparedData?.totalLoss.series || []).map(
    (p) => ({
      key: p.key,
      totalLoss: p.value,
      totalLossToCompare: p.valueToCompare,
    })
  );
  const deliveryLossSeriesCompared = (
    comparedData?.deliveryLoss?.series || []
  ).map((p) => ({
    key: p.key,
    deliveryLoss: p.value,
    deliveryLossToCompare: p.valueToCompare,
  }));

  // ── Totals / counts (cents) ──────────────────────────────────────────────
  const grossSum = clampPositive(
    isComparing ? comparedData?.gross.total ?? 0 : data?.gross ?? 0
  );
  const grossSumToCompare = clampPositive(
    comparedData?.gross.totalToCompare ?? 0
  );

  const totalIncomeSum = clampPositive(
    isComparing ? comparedData?.totalIncome.total ?? 0 : data?.totalIncome ?? 0
  );
  const totalIncomeSumToCompare = clampPositive(
    comparedData?.totalIncome.totalToCompare ?? 0
  );

  const totalExpensesSum = isComparing
    ? comparedData?.expenses.total ?? 0
    : data?.expenses ?? 0;
  const totalExpensesSumToCompare = comparedData?.expenses.totalToCompare ?? 0;

  const totalLossSum = isComparing
    ? comparedData?.totalLoss.total ?? 0
    : data?.totalLoss ?? 0;
  const totalLossSumToCompare = comparedData?.totalLoss.totalToCompare ?? 0;

  const ordersAtLossCount = isComparing
    ? comparedData?.ordersAtLoss.total ?? 0
    : data?.ordersAtLoss ?? 0;

  const deliveryLossSum = isComparing
    ? comparedData?.deliveryLoss?.total ?? 0
    : data?.deliveryLoss ?? 0;
  const deliveryLossSumToCompare =
    comparedData?.deliveryLoss?.totalToCompare ?? 0;
  const deliveriesAtLossCount = isComparing
    ? comparedData?.deliveriesAtLoss?.total ?? 0
    : data?.deliveriesAtLoss ?? 0;

  // Compare-aware tooltip — shows both current and previous-period values.
  // (Gross/Income/Expenses tooltips live inside their tabbed cards.)
  const LossTooltip = buildSingleMetricCompareTooltip({
    dataKey: 'totalLoss',
    label: 'Total Loss',
    color: lightTheme.primary,
    negateForDisplay: true,
  });
  // The window every headline on these cards covers. Restated on the cards
  // themselves because the only other place it appears is the range picker at
  // the top of the page, which does not travel with a screenshot of a card.
  const periodLabel = renderDateLabelWithToday(from, to);
  // Monthly Saas on both revenue cards opens the same details.
  const [saasDetailsOpen, setSaasDetailsOpen] = useState(false);
  const openSaasDetails = () => setSaasDetailsOpen(true);
  // So does Service Fee.
  const [serviceFeeDetailsOpen, setServiceFeeDetailsOpen] = useState(false);
  const openServiceFeeDetails = () => setServiceFeeDetailsOpen(true);

  const DeliveryLossTooltip = buildSingleMetricCompareTooltip({
    dataKey: 'deliveryLoss',
    label: 'Delivery Loss',
    color: lightTheme.primary,
    negateForDisplay: true,
  });

  return (
    <Div $relative>
      {loading && (
        <AdminSpinner small absolute overflowBorderRadius="20px" creme />
      )}
      <DragScrollX width={!isAdmin ? 'calc(50% - 12px)' : undefined}>
        {/* Charts 1–4 (Profit & Loss, Gross, Total Income, Operating Expenses)
            are admin-only. Support sees just the two loss charts below. */}
        {isAdmin && (
          <>
            {/* 1. Profit & Loss (diverging profit/loss, net as headline) */}
            <ProfitAndLossChart
              data={data}
              comparedData={comparedData}
              groupByType={groupByType}
              periodTypeComparedTo={periodTypeComparedTo}
            />

            {/* 2. Gross — total sales before fees (top line), tabbed Pie/Graph.
                Breakdown (vGrubs / vOrders) comes only from the single-period
                overview. */}
            <GrossCard
              grossSeries={grossSeries}
              grossSeriesCompared={grossSeriesCompared}
              grossSum={grossSum}
              grossSumToCompare={grossSumToCompare}
              groupByType={groupByType}
              isComparing={isComparing}
              grossBreakdown={data?.grossBreakdown}
              vGrubsProviderBreakdown={data?.vGrubsProviderBreakdown}
              periodLabel={periodLabel}
              onOpenSaasDetails={openSaasDetails}
              onOpenServiceFeeDetails={openServiceFeeDetails}
            />

            {/* 3. Total Income — tabbed card combining the area chart (Trend)
                and the services breakdown pie (Breakdown). The pie tab is locked
                out in compare mode since /compare doesn't return servicesBreakdown. */}
            <TotalIncomeCard
              incomeSeries={incomeSeries}
              incomeSeriesCompared={incomeSeriesCompared}
              totalIncomeSum={totalIncomeSum}
              totalIncomeSumToCompare={totalIncomeSumToCompare}
              groupByType={groupByType}
              isComparing={isComparing}
              servicesBreakdown={data?.servicesBreakdown}
              vGrubsProviderBreakdown={data?.vGrubsProviderBreakdown}
              periodLabel={periodLabel}
              onOpenSaasDetails={openSaasDetails}
              onOpenServiceFeeDetails={openServiceFeeDetails}
            />

            {/* 3. Operating Expenses — tabbed card (Pie: Sales Commissions / vDrive VR
            Graph: trend). Breakdown comes only from the
            single-period overview. */}
            <OperatingExpensesCard
              expensesSeries={expensesSeries}
              expensesSeriesCompared={expensesSeriesCompared}
              totalExpensesSum={totalExpensesSum}
              totalExpensesSumToCompare={totalExpensesSumToCompare}
              groupByType={groupByType}
              isComparing={isComparing}
              expensesBreakdown={data?.expensesBreakdown}
              customExpenses={data?.customExpenses ?? []}
              onExpensesChanged={onDataChange}
              periodLabel={periodLabel}
            />

            {/* 5. VR Order Loss — combined loss $ / orders count */}
            <ReusableChart
              isComparing={isComparing}
              chartType="area"
              title="Order Loss"
              currency={NumberType.USD}
              totalValue={totalLossSum}
              totalValueToCompare={totalLossSumToCompare}
              valueColor={lightTheme.primary}
              data={lossSeries}
              dataToCompare={lossSeriesCompared}
              groupByType={groupByType}
              chartFields={[
                createField('totalLoss', lightTheme.primary, { stackId: '1' }),
              ]}
              chartFieldsToCompare={[
                createField('totalLoss', lightTheme.primary, { stackId: '2' }),
                createField('totalLossToCompare', lightTheme.darkGray, {
                  stackId: '1',
                }),
              ]}
              customYAxisTick={CurrencyYAxisTick}
              customXAxisTick={CustomXAxisTick}
              customTooltip={LossTooltip}
              customTotalValue={
                <>
                  {formatAmount(-totalLossSum, NumberType.USD)}
                  <span style={{ color: lightTheme.lightGray }}>
                    {' '}
                    / {ordersAtLossCount}
                  </span>
                </>
              }
              customComponent={<SeeLossOrdersButton from={from} to={to} />}
              chartHeight="300px"
              chartHeightSM="315px"
            />

            {/* 6. Delivery Loss — informational KPI: SUM(realDspFee − dspFee) over
            completed deliveries where the DSP charged us more. Already
            reflected in vDrive net, NOT added to overall loss/profitAndLoss. */}
            <ReusableChart
              isComparing={isComparing}
              chartType="area"
              title="Delivery Loss"
              currency={NumberType.USD}
              totalValue={deliveryLossSum}
              totalValueToCompare={deliveryLossSumToCompare}
              valueColor={lightTheme.primary}
              data={deliveryLossSeries}
              dataToCompare={deliveryLossSeriesCompared}
              groupByType={groupByType}
              chartFields={[
                createField('deliveryLoss', lightTheme.primary, {
                  stackId: '1',
                }),
              ]}
              chartFieldsToCompare={[
                createField('deliveryLoss', lightTheme.primary, {
                  stackId: '2',
                }),
                createField('deliveryLossToCompare', lightTheme.darkGray, {
                  stackId: '1',
                }),
              ]}
              customYAxisTick={CurrencyYAxisTick}
              customXAxisTick={CustomXAxisTick}
              customTooltip={DeliveryLossTooltip}
              customTotalValue={
                <>
                  {formatAmount(-deliveryLossSum, NumberType.USD)}
                  <span style={{ color: lightTheme.lightGray }}>
                    {' '}
                    / {deliveriesAtLossCount}
                  </span>
                </>
              }
              customComponent={<SeeDeliveryLossesButton from={from} to={to} />}
              chartHeight="300px"
              chartHeightSM="315px"
            />
          </>
        )}
      </DragScrollX>

      {isAdmin && (
        <SaasDetailsModal
          isOpen={saasDetailsOpen}
          closeModal={() => setSaasDetailsOpen(false)}
          from={from}
          to={to}
          periodLabel={periodLabel}
        />
      )}
      {isAdmin && (
        <ServiceFeeDetailsModal
          isOpen={serviceFeeDetailsOpen}
          closeModal={() => setServiceFeeDetailsOpen(false)}
          from={from}
          to={to}
          periodLabel={periodLabel}
        />
      )}
    </Div>
  );
};
